import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogContentText, Typography, Box, IconButton, DialogActions, DialogContent } from '@mui/material';
import swiftbot from '../../../assets/swiftbot.png';
import { customLog, formatDate } from '../../../utils/utils';
import CloseIcon from '@mui/icons-material/Close';
import { setModalClose, setFireAlarmOff, setFireAlarmOn, triggerFire } from '../../../store/fire/fireTriggerSlice';
import { setNotificationOn, setNotificationOff, setNotificationMessage, setNotificationTitle, setNotificationType, setNotificationColorProps } from '../../../store/notification/notificationSlice';
import { useSelector, useDispatch } from 'react-redux';
import { NotificationType, RobotData3, RobotInfo3, RobotStatus2 } from '../../../store/types';
import { useWebSocket } from '../../../components/useWebSocket';
import { getProfile } from '../../../utils/api';
import { VIEW } from '../../../utils/breakpoint';
import { ErrorOutline, StopCircle } from '@mui/icons-material';
import { FIRE_ALARM_USE_ICON } from '../../../utils/const';
import estopIcon from '../../../assets/info.png';
import claningIcon from '../../../assets/cleaning/clean-icon.png'
import parcel from '../../../assets/parcel.png';
import FireAlarmIcon from '../../../components/icons/FireAlarmIcon';
import { getJobLogs } from '../../../lib/api/job';
import { JobEnums } from '../../../utils/enums/JobEnums';
import { closeSnackbar } from 'notistack';
import { CLEANING_NOTIFICATION_TYPE } from '../../../components/Notification';

interface NotificationProps {
    open?: boolean;
    message?: string;
    onClose?: (id: number) => void;
    onDismissAlarm?: () => void;
    imageUrl: string;
}

interface colorProps {
    background: string;
    border: string;
}

export const successColor : colorProps = {
    background: "rgba(0, 255, 0, 0.2)",
    border: "10px solid #0BFA4E"
}

export const criticalColor : colorProps = {
    background: "rgba(255, 0, 0, 0.2)",
    border: "10px solid #FF0000"
}

const Notification: React.FC<NotificationProps> = ({ open, message, onClose, onDismissAlarm, imageUrl }) => {
    const notificationType = useSelector((state: any) => state.notification.notificationType);
    const notificationMessage = useSelector((state: any) => state.notification.notificationMessage);
    const notificationTitle = useSelector((state: any) => state.notification.notificationTitle);
    const isNotificationOn = useSelector((state: any) => state.notification.isNotificationOn);
    const currentNotification: NotificationType = useSelector((state: any) => state.notification.currentNotification);
    // const isNotificationOn = useSelector((state: any) => state.notification.isNotificationOn);
    const notificationQueue = useSelector((state: any) => state.notification.notificationsQueue);
    const isEstop = currentNotification && currentNotification.notification_details.title.toLowerCase().includes("e-stop");

    const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);

    const handleRobotStatus = (data: RobotStatus2) => {
      if (!currentNotification || !isEstop) {
        return
      }
      const robotId = data.robot_id;
      const robotIdCurrentNotification = JSON.parse((currentNotification as unknown as { meta: string}).meta).robot_id;

      if (data.robot_state !== 'estop' && (robotId && robotIdCurrentNotification)) {
        dismissNotification()
      }
    }

    const robotStatus = `${process.env.REACT_APP_WEBSOCKET_URL}/robot/${getProfile()?.organisation}/`;
    useWebSocket(robotStatus, handleRobotStatus);

    const dispatch = useDispatch();
    const isErrorNotifType = ["fire", "failed", "robot update"].includes(notificationType.toLowerCase()) || isEstop;
    const isFireNotifType = notificationType.toLowerCase() === "fire";
    const isCleaningNotifType = currentNotification.type === CLEANING_NOTIFICATION_TYPE
    
    const colorProps = isErrorNotifType ? criticalColor : successColor;

    const handleOnClose = () => {
        onDismissAlarm?.()
    }

    useEffect(() => {
        customLog("currentNotification", currentNotification);
        customLog("notificationQueue", notificationQueue);
    }, [currentNotification, notificationQueue]);

    const checkCurrentStatus = useCallback(async () => {
      if (!currentNotification || currentNotification.notification_details.notification_type !== 'job_notification') {
        return
      }

      const orderNumber = JSON.parse((currentNotification as unknown as { meta: string}).meta).order_number;
      
      const response = await getJobLogs(orderNumber);
      const getLogs = response?.data || [];


      const orderByTimestamps = getLogs.sort((a: any, b: any) => b.timestamp - a.timestamp);

      const latestLog = orderByTimestamps[0];

      // console.log("HANDS LOGS", getLogs);
      const jobStatus = latestLog?.job_status || '';

      if ([JobEnums.ITEM_DELIVERED, JobEnums.DELIVERING].includes(jobStatus)) {
        if (timerInterval) {
          clearInterval(timerInterval);
        }
        
        closeSnackbar();
        dismissNotification()
      }
      
    }, [currentNotification]);

    const jobOverviewUrl = `${process.env.REACT_APP_WEBSOCKET_URL}/delivery/overview/${getProfile()?.uuid}/`;
    useWebSocket(jobOverviewUrl, checkCurrentStatus);

    useEffect(() => {
      if (!currentNotification || currentNotification.notification_details.notification_type !== 'job_notification') {
        return
      }

      const timer = setInterval(() => {
        checkCurrentStatus();
      }, 30 * 1000); // Check every 30 seconds
      setTimerInterval(timer);

      return () => {
        if (timerInterval) {
          clearInterval(timerInterval);
        } else {
          clearInterval(timer);
        }
      };
    }, [currentNotification]);

    
    const dismissNotification = () => {
      if (!currentNotification) {
        return
      }

      if (timerInterval) {
        clearInterval(timerInterval);
      }
      onClose?.(currentNotification.id)
    }

    if (!currentNotification) {
      customLog("No notification", currentNotification);
    }

    const renderIcon = () => {
      const notificationTitleLower = currentNotification.notification_details.title.toLowerCase();
      if (isCleaningNotifType && FIRE_ALARM_USE_ICON) {
        return (
          <Box sx={{ width: '120px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <img src={claningIcon} alt="cleaning-notif" style={{ height: "100px", width: '100px', objectFit: 'scale-down'}} />
          </Box>
        )
      }
      if (isFireNotifType && FIRE_ALARM_USE_ICON) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <FireAlarmIcon />
          </Box>
        )
      }
      if (isEstop && FIRE_ALARM_USE_ICON) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <img src={estopIcon} alt="swiftbot" style={{ height: "120px", width: '120px', objectFit: 'scale-down'}} />
          </Box>
        )
      }
      if (notificationTitleLower.includes('arrived') && FIRE_ALARM_USE_ICON) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <img src={parcel} alt="swiftbot" style={{ height: "120px", width: '120px', objectFit: 'scale-down'}} />
          </Box>
        )
      }
      if (notificationTitleLower.includes('error') && FIRE_ALARM_USE_ICON) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <img src={estopIcon} alt="swiftbot" style={{ height: "120px", width: '120px', objectFit: 'scale-down'}} />
          </Box>
        )
      }
      return <img src={imageUrl} alt="swiftbot" style={{ height: "100%"}} />
    }

    return currentNotification ? (
        <div>
            <style>
                {`
                @keyframes blinkBackdrop {
                    0%, 100% {
                        background-color: ${colorProps.background}; // Red semi-transparent
                    }
                    50% {
                        background-color: rgba(255, 0, 0, 0); // Fully transparent
                    }
                }
                .blink-backdrop {
                    animation: blinkBackdrop 2s linear infinite;
                }
                `}
            </style>
            <Dialog
                open={isNotificationOn}
                maxWidth={false}
                BackdropProps={{ className: 'blink-backdrop' }}
                sx={(theme) => ({
                    '& .MuiDialog-paper': {
                      [theme.breakpoints.up(VIEW.PHONE)]: {
                        left: '5px',
                        right: '5px',
                        mx: 'auto',
                        width: '90vw',
                        maxWidth: 'none'
                      },
                      [theme.breakpoints.up(VIEW.TABLET)]: {
                        width: '600px',
                      },
                      [theme.breakpoints.up(VIEW.DESKTOP)]: {
                        left: '50%',  // Center horizontally
                        right: '50%',
                        transform: 'translate(-50%, -10%)',
                        height: '250px',
                        width: '600px',
                      },
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        position: 'absolute',  // Use absolute positioning
                        top: '10%',  // Adjust this value to control the vertical position
                    }
                })}
            >
                <Box style={{
                    border: `${colorProps.border}`,
                    borderRadius: "15px",
                    backgroundColor: "#ffffff",
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        margin: 'auto',
                        bgcolor: "#fff",
                        p: 2,
                        px: isFireNotifType ? '30px' : 2
                    }}>
                        <IconButton
                            // onClick={() => { dispatch(setNotificationOff()) }}
                            onClick={() => dismissNotification()}
                            // if fire alarm prompt user to confirm before disabling
                            // onClick={() => { notificationType === "fire" ? dispatch(setFireAlarmOff()) : dispatch(setNotificationOff()) }}
                            sx={{
                                position: 'absolute',
                                right: 20,
                                top: 20,
                                color: "#727272"
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box sx={(theme) => ({
                          [theme.breakpoints.down(VIEW.TABLET)]: {
                            flex: 'auto',
                            width: '100%'
                          },
                          [theme.breakpoints.up(VIEW.TABLET)]: {
                            ml: isFireNotifType ? 0 : 3,
                          },
                          mr: isFireNotifType ? 0 : 2,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          height: isFireNotifType ? '160px' : 'auto',
                          })}>
                            <Box sx={(theme) => ({
                              [theme.breakpoints.up(VIEW.PHONE)]: {
                                height: "80px",
                                display: 'none'
                              },
                              [theme.breakpoints.up(VIEW.TABLET)]: {
                                height: isFireNotifType? "120px" : "160px",
                                display: 'inline-block',
                                maxWidth: '200px',
                                flex: 'none'
                              },
                            })}>
                                {renderIcon()}
                            </Box>
                            <DialogContent sx={(theme) => ({
                              [theme.breakpoints.up(VIEW.TABLET)]: {
                                ml: 3,
                                mb: isFireNotifType ? 0 : 2
                              },
                                textAlign: "left",
                                fontSize: "18px",
                                fontWeight: "bold",
                                color: "#000",
                                display: "flex",
                                justifyContent: isFireNotifType ? "center" : "left",
                                alignItems: "left",
                                flexDirection: "column",
                                padding: 0
                            })}>
                                {/* Order has been delivered */}
                                <Typography fontSize={'18px'} fontWeight={700}>
                                  {currentNotification?.notification_details.title}
                                </Typography>
                                <Typography variant="h5" component="div" sx={{
                                    color: "#727272",
                                    fontSize: "16px",
                                    textAlign: "left",
                                    mt: isFireNotifType ? 1.5 : 2
                                }}>
                                    {/* Please attend to the robot and receive the item at the TR+43 */}
                                    {currentNotification?.notification_details.description}
                                </Typography>
                            { notificationType === "fire" &&    
                              <Box>
                                <Button variant="contained" sx={{
                                    // position: 'absolute',
                                    // bottom: 70,
                                    // left: 185,
                                    width: '100%',
                                    mt: '12px',
                                    color: "#fff",
                                    backgroundColor: "#eb4034",
                                    '&:hover': {
                                        backgroundColor: "#b82218"
                                    }
                                }}
                                    onClick={handleOnClose}
                                >
                                    Disable Fire Alarm
                                </Button>
                              </Box>
                            }
                            </DialogContent>
                        </Box>
                        <Box sx={{ }}>
                          <Typography variant="h6" component="div" sx={{
                              flexGrow: 1,
                              color: "#727272",
                              fontSize: "16px",
                              textAlign: "right",
                              mt: isFireNotifType ? -2 : 1
                          }}>
                              {/* {notificationType === "fire" ? formatDate(Date.now()/1000) : formatDate(currentNotification?.notification_details.created_timestamp)} */}
                              { formatDate(currentNotification?.notification_details.created_timestamp)}
                              {/* {formatDate(Date.now()/1000)} */}
                          </Typography>
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </div>
    ) : (<></>);
}

export default Notification;
